// Copyright (c) Docugami, Inc. All rights reserved.

import { DgButton } from "components/common/DgCommonStyledComponents";
import { primaryColorDark } from "components/shell/presentation/AppStyles";
import { ContextualMenu, IContextualMenuItem } from "@fluentui/react";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { IDocumentCommand } from "../DocumentCommand";

const CommandBarContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 10px;
    flex: 0 0 auto;
    white-space: nowrap;
    margin-left: auto;
`

interface DocumentCommandBarProps {
    commands: IDocumentCommand[]
    disable?: boolean;
    overflow?: IDocumentCommand[]
}

interface DocumentCommandSet {
    id: string;
    commands: IDocumentCommand[];
}

export const DocumentCommandBar = (props: DocumentCommandBarProps): JSX.Element => {
    const [commandSets, setCommandSets] = useState<DocumentCommandSet[]>([]);

    const isCommandActive = props.commands.filter(c => c.isActive).length > 0;
    const disableCommands = !!props.disable || isCommandActive;

    useEffect(() => {
        setCommandSets(createCommandSets(props.commands));
    }, [props.commands]);

    return (
        <CommandBarContainer>
            {
                commandSets.map(set => {
                    if (set.commands.length > 1) {
                        // Composite command
                        return <CommandSetButton
                            key={set.id}
                            label={set.id}
                            commands={set.commands}
                            disabled={!!props.disable}
                            disableCommands={disableCommands} />
                    }
                    else {
                        const command = set.commands[0];
                        return <DgButton
                            key={command.id}
                            name={command.label}
                            disabled={disableCommands || !command.isEnabled}
                            onClick={command.execute}>{command.label.toUpperCase()}</DgButton>
                    }

                })
            }
            {props.overflow && props.overflow.length ? <CommandSetButton commands={props.overflow} disabled={!!props.disable} disableCommands={disableCommands} label="..." /> : null}
        </CommandBarContainer>
    )
}

function createCommandSets(commands: IDocumentCommand[]): DocumentCommandSet[] {
    const sets: DocumentCommandSet[] = []
    commands.forEach(c => {
        let set = !!c.set ? sets.find(x => x.id === c.set) : undefined;
        if (!set) {
            set = {
                id: c.set ?? c.id,
                commands: [],
            }

            sets.push(set);
        }

        set.commands.push(c);
    });

    return sets;
}

function getContextualMenuItems(commands: IDocumentCommand[], disabled: boolean): IContextualMenuItem[] {
    return commands.map(c => {
        return {
            key: c.id,
            name: c.label,
            onClick: c.execute,
            disabled: disabled || !c.isEnabled
        }
    });
}

const CommandButton = styled(DgButton) <{ isOpen: boolean }>`
    background: ${props => props.isOpen ? primaryColorDark : ''};
    :hover {
        background: ${props => props.isOpen ? primaryColorDark : ''};
    }
`

interface CommandSetButtonProps {
    commands: IDocumentCommand[]
    disabled: boolean;
    disableCommands: boolean;
    label: string;
}

const CommandSetButton = (props: CommandSetButtonProps): JSX.Element => {
    const [isOpen, setIsOpen] = useState(false);

    const menuAnchorRef = useRef<HTMLButtonElement>(null);
    return (
        <div>
            <CommandButton isOpen={isOpen} onClick={() => setIsOpen(true)} ref={menuAnchorRef}>
                {props.label.toLocaleUpperCase()}
            </CommandButton>
            <ContextualMenu hidden={!isOpen} onDismiss={() => setIsOpen(false)} target={menuAnchorRef} items={getContextualMenuItems(props.commands, props.disabled)} />
        </div>
    );
}
